// frontend/src/pages/superAdmin/ResponseSummary.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';


const ResponseSummary = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [summaryData, setSummaryData] = useState([]);

  // Fetch fiscal years when component mounts
  useEffect(() => {
    const fetchFiscalYears = async () => {
      try {
        const response = await api.get('/fiscal-years');
        setFiscalYears(response.data);
        
        // Set active fiscal year as default
        const activeFiscalYear = response.data.find(year => year.active);
        if (activeFiscalYear) {
          setSelectedFiscalYear(activeFiscalYear.id);
          fetchSummaryData(activeFiscalYear.id);
        } else if (response.data.length > 0) {
          setSelectedFiscalYear(response.data[0].id);
          fetchSummaryData(response.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching fiscal years:', error);
        showError('ไม่สามารถดึงข้อมูลปีงบประมาณได้');
      }
    };

    fetchFiscalYears();
  }, []);

  // Fetch summary data when fiscal year changes
  const fetchSummaryData = async (fiscalYearId) => {
    setLoading(true);
    try {
      const response = await api.get(`/quotas/${fiscalYearId}`);
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error fetching summary data:', error);
      showError('ไม่สามารถดึงข้อมูลสรุปได้');
    } finally {
      setLoading(false);
    }
  };

  // Handle fiscal year change
  const handleFiscalYearChange = (e) => {
    const fiscalYearId = e.target.value;
    setSelectedFiscalYear(fiscalYearId);
    fetchSummaryData(fiscalYearId);
  };

  // Show error message
  const showError = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'เกิดข้อผิดพลาด',
      text: message,
      confirmButtonText: 'ตกลง'
    });
  };

  // Calculate progress percentage
  const calculateProgress = (current, quota) => {
    if (quota === 0) return 100;
    const percentage = (current / quota) * 100;
    return Math.min(percentage, 100);
  };

  // Get status text based on progress
  const getStatusText = (current, quota) => {
    if (quota === 0) return 'ไม่กำหนด';
    if (current >= quota) return 'เสร็จสมบูรณ์';
    if (current > 0) return 'กำลังดำเนินการ';
    return 'ยังไม่เริ่ม';
  };

  // Get status class based on progress
  const getStatusClass = (current, quota) => {
    if (quota === 0) return 'secondary';
    if (current >= quota) return 'success';
    if (current > 0) return 'warning';
    return 'danger';
  };

  // Calculate total quota and responses for each school
  const calculateTotals = (school) => {
    const totalQuota = school.iit_quota + school.eit1_quota + school.eit2_quota;
    const totalResponses = school.iit_count + school.eit1_count + school.eit2_count;
    return { totalQuota, totalResponses };
  };


  // เพิ่มฟังก์ชันนี้สำหรับการส่งออกเป็น Excel
  const exportToExcel = () => {
    // หาข้อมูลปีงบประมาณที่กำลังเลือก
    const currentFiscalYear = fiscalYears.find(year => year.id === selectedFiscalYear);
    const fiscalYearText = currentFiscalYear ? `ปีงบประมาณ_${currentFiscalYear.year}` : 'รายงาน';
    
    // สร้างข้อมูลสำหรับ Excel
    const excelData = summaryData.map((school, index) => {
      const { totalQuota, totalResponses } = calculateTotals(school);
      
      return {
        'ลำดับ': index + 1,
        'โรงเรียน': school.school_name,
        'IIT เป้าหมาย': school.iit_quota,
        'IIT ตอบแล้ว': school.iit_count,
        'IIT สถานะ': getStatusText(school.iit_count, school.iit_quota),
        'EIT1 เป้าหมาย': school.eit1_quota,
        'EIT1 ตอบแล้ว': school.eit1_count,
        'EIT1 สถานะ': getStatusText(school.eit1_count, school.eit1_quota),
        'EIT2 เป้าหมาย': school.eit2_quota,
        'EIT2 ตอบแล้ว': school.eit2_count,
        'EIT2 สถานะ': getStatusText(school.eit2_count, school.eit2_quota),
        'รวมเป้าหมาย': totalQuota,
        'รวมตอบแล้ว': totalResponses,
        'รวมสถานะ': getStatusText(totalResponses, totalQuota),
      };
    });
    
    // สร้าง worksheet จากข้อมูล
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // ปรับความกว้างของคอลัมน์ให้เหมาะสม
    const columnWidths = [
      { wch: 5 },   // ลำดับ
      { wch: 30 },  // โรงเรียน
      { wch: 12 },  // IIT เป้าหมาย
      { wch: 10 },  // IIT ตอบแล้ว
      { wch: 15 },  // IIT สถานะ
      { wch: 12 },  // EIT1 เป้าหมาย
      { wch: 10 },  // EIT1 ตอบแล้ว
      { wch: 15 },  // EIT1 สถานะ
      { wch: 12 },  // EIT2 เป้าหมาย
      { wch: 10 },  // EIT2 ตอบแล้ว
      { wch: 15 },  // EIT2 สถานะ
      { wch: 12 },  // รวมเป้าหมาย
      { wch: 10 },  // รวมตอบแล้ว
      { wch: 15 },  // รวมสถานะ
    ];
    worksheet['!cols'] = columnWidths;
    
    // สร้าง workbook และเพิ่ม worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'รายงานสรุปผู้ตอบแบบสอบถาม');
    
    // สร้างวันที่ปัจจุบันสำหรับชื่อไฟล์
    const date = new Date();
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    // ส่งออกและดาวน์โหลดไฟล์
    XLSX.writeFile(workbook, `สรุปผู้ตอบแบบสอบถาม_${fiscalYearText}_${dateStr}.xlsx`);
    
    // แสดงข้อความแจ้งเตือนเมื่อดาวน์โหลดสำเร็จ
    Swal.fire({
      icon: 'success',
      title: 'ส่งออกข้อมูลสำเร็จ',
      text: 'ไฟล์ Excel ได้ถูกดาวน์โหลดเรียบร้อยแล้ว',
      confirmButtonText: 'ตกลง'
    });
  };

  // Render content
  return (
    <div className="wrapper">
      <Header user={user} title="รายงานสรุปผู้ตอบแบบสอบถาม" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>รายงานสรุปผู้ตอบแบบสอบถาม</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">รายงานสรุปผู้ตอบแบบสอบถาม</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">สรุปจำนวนผู้ตอบแบบสอบถาม</h3>
                
                <div className="card-tools">
                  <div className="form-group mb-0">
                    <select 
                      className="form-control" 
                      value={selectedFiscalYear || ''} 
                      onChange={handleFiscalYearChange}
                    >
                      <option value="">เลือกปีงบประมาณ</option>
                      {fiscalYears.map(year => (
                        <option key={year.id} value={year.id}>
                          ปีงบประมาณ {year.year} {year.active ? '(ปัจจุบัน)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="card-body">
                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">กำลังโหลด...</span>
                    </div>
                    <p className="mt-2">กำลังโหลดข้อมูล...</p>
                  </div>
                ) : summaryData.length === 0 ? (
                  <div className="alert alert-info">
                    ไม่พบข้อมูลสำหรับปีงบประมาณที่เลือก
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr className="bg-navy">
                          <th rowSpan="2" className="text-center align-middle">ลำดับ</th>
                          <th rowSpan="2" className="text-center align-middle">โรงเรียน</th>
                          <th colSpan="3" className="text-center">แบบสอบถาม IIT</th>
                          <th colSpan="3" className="text-center">แบบสอบถาม EIT1</th>
                          <th colSpan="3" className="text-center">แบบสอบถาม EIT2</th>
                          <th colSpan="3" className="text-center">รวมทั้งหมด</th>
                        </tr>
                        <tr className="bg-navy">
                          <th className="text-center">เป้าหมาย</th>
                          <th className="text-center">ตอบแล้ว</th>
                          <th className="text-center">สถานะ</th>
                          <th className="text-center">เป้าหมาย</th>
                          <th className="text-center">ตอบแล้ว</th>
                          <th className="text-center">สถานะ</th>
                          <th className="text-center">เป้าหมาย</th>
                          <th className="text-center">ตอบแล้ว</th>
                          <th className="text-center">สถานะ</th>
                          <th className="text-center">เป้าหมาย</th>
                          <th className="text-center">ตอบแล้ว</th>
                          <th className="text-center">สถานะ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.map((school, index) => {
                          // Calculate totals for each school
                          const { totalQuota, totalResponses } = calculateTotals(school);
                          
                          return (
                            <tr key={school.school_id}>
                              <td className="text-center">{index + 1}</td>
                              <td>{school.school_name}</td>
                              
                              {/* IIT */}
                              <td className="text-center">{school.iit_quota}</td>
                              <td className="text-center">{school.iit_count}</td>
                              <td className="text-center">
                                <div className="d-flex flex-column">
                                  <span className={`badge bg-${getStatusClass(school.iit_count, school.iit_quota)}`}>
                                    {getStatusText(school.iit_count, school.iit_quota)}
                                  </span>
                                  <div className="progress mt-1" style={{ height: '5px' }}>
                                    <div 
                                      className={`progress-bar bg-${getStatusClass(school.iit_count, school.iit_quota)}`} 
                                      style={{ width: `${calculateProgress(school.iit_count, school.iit_quota)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </td>
                              
                              {/* EIT1 */}
                              <td className="text-center">{school.eit1_quota}</td>
                              <td className="text-center">{school.eit1_count}</td>
                              <td className="text-center">
                                <div className="d-flex flex-column">
                                  <span className={`badge bg-${getStatusClass(school.eit1_count, school.eit1_quota)}`}>
                                    {getStatusText(school.eit1_count, school.eit1_quota)}
                                  </span>
                                  <div className="progress mt-1" style={{ height: '5px' }}>
                                    <div 
                                      className={`progress-bar bg-${getStatusClass(school.eit1_count, school.eit1_quota)}`} 
                                      style={{ width: `${calculateProgress(school.eit1_count, school.eit1_quota)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </td>
                              
                              {/* EIT2 */}
                              <td className="text-center">{school.eit2_quota}</td>
                              <td className="text-center">{school.eit2_count}</td>
                              <td className="text-center">
                                <div className="d-flex flex-column">
                                  <span className={`badge bg-${getStatusClass(school.eit2_count, school.eit2_quota)}`}>
                                    {getStatusText(school.eit2_count, school.eit2_quota)}
                                  </span>
                                  <div className="progress mt-1" style={{ height: '5px' }}>
                                    <div 
                                      className={`progress-bar bg-${getStatusClass(school.eit2_count, school.eit2_quota)}`} 
                                      style={{ width: `${calculateProgress(school.eit2_count, school.eit2_quota)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </td>
                              
                              {/* Total (รวมทั้งหมด) */}
                              <td className="text-center font-weight-bold">{totalQuota}</td>
                              <td className="text-center font-weight-bold">{totalResponses}</td>
                              <td className="text-center">
                                <div className="d-flex flex-column">
                                  <span className={`badge bg-${getStatusClass(totalResponses, totalQuota)}`}>
                                    {getStatusText(totalResponses, totalQuota)}
                                  </span>
                                  <div className="progress mt-1" style={{ height: '5px' }}>
                                    <div 
                                      className={`progress-bar bg-${getStatusClass(totalResponses, totalQuota)}`} 
                                      style={{ width: `${calculateProgress(totalResponses, totalQuota)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="card-footer">
                <div className="row">
                  <div className="col-md-6">
                    <div className="d-flex gap-3">
                      <div>
                        <span className="badge bg-success">เสร็จสมบูรณ์</span> - ตอบครบตามเป้าหมาย
                      </div>
                      <div>
                        <span className="badge bg-warning">กำลังดำเนินการ</span> - อยู่ระหว่างตอบแบบสอบถาม
                      </div>
                      <div>
                        <span className="badge bg-danger">ยังไม่เริ่ม</span> - ยังไม่มีการตอบแบบสอบถาม
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 text-end">
                  <button 
              className="btn btn-success"
              onClick={exportToExcel}
              disabled={loading || summaryData.length === 0}
            >
              <i className="fas fa-file-excel me-2"></i>
              ส่งออกเป็น Excel
            </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ResponseSummary;